import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';

function money(n) {
  if (n == null) return '—';
  return `USD ${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function IntercompanyAuditPage() {
  const [jobs, setJobs] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);
  const [approving, setApproving] = useState(false);
  const [verifiedHours, setVerifiedHours] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');

  const load = useCallback(async () => {
    const res = await api.listSubcontractCosts();
    setJobs(res || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load subcontracted execution history.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const audit = async (job) => {
    setPageMsg(null);
    setDetail(null);
    try {
      const d = await api.getSubcontractCost(job.jobId);
      setDetail(d);
      setVerifiedHours(String(d.verifiedHours ?? d.loggedHours ?? ''));
      setAdjustmentReason('');
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to open audit.' });
    }
  };

  const raiseSap = async () => {
    if (!detail) return;
    setApproving(true);
    setPageMsg(null);
    try {
      const res = await api.raiseSubcontractToSap(detail.jobId);
      const updated = await api.getSubcontractCost(detail.jobId);
      setDetail(updated);
      await load();
      setPageMsg({
        type: 'success',
        text: `Affiliate billing raised to SAP — Sales Order ${res.sapSalesOrderId}.`,
      });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to raise affiliate billing to SAP.' });
    } finally {
      setApproving(false);
    }
  };

  const approve = async () => {
    if (!detail) return;
    setApproving(true);
    setPageMsg(null);
    try {
      const payload = {};
      const vh = Number(verifiedHours);
      if (verifiedHours !== '' && Number.isFinite(vh)) payload.verifiedHours = vh;
      if (adjustmentReason.trim()) payload.adjustmentReason = adjustmentReason.trim();
      const res = await api.approveSubcontractCost(detail.jobId, payload);
      const updated = await api.getSubcontractCost(detail.jobId);
      setDetail(updated);
      await load();
      setPageMsg({
        type: 'success',
        text: `Subcontract costs approved — ${money(res.calculatedCost)} settled for ${detail.operatorName}.`,
      });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to approve subcontract costs.' });
    } finally {
      setApproving(false);
    }
  };

  const hoursChanged =
    detail && verifiedHours !== '' && Number(verifiedHours) !== Number(detail.loggedHours);

  return (
    <div className="container-fluid py-4" data-testid="intercompany-audit-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-handshake me-2" aria-hidden="true" />
                Affiliate Cost Audit &amp; Settlement
              </h3>
              <p className="text-muted mb-0 small">
                Review computed inter-company costs for subcontracted affiliate jobs and approve the
                settlement for billing.
              </p>
            </div>
            <span className="badge bg-info text-dark p-2">{jobs.length} Subcontracts</span>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="intercompany-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="intercompany-message">
          {pageMsg.text}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* LEFT: subcontracted execution history */}
          <div className="col-lg-7 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-history me-2" aria-hidden="true" />
                  Subcontracted Execution History
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light text-secondary small text-uppercase">
                      <tr>
                        <th>Subcontract ID</th>
                        <th>Affiliate Operator</th>
                        <th>Duration</th>
                        <th>Calculated Cost</th>
                        <th aria-label="action" />
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-4">
                            No subcontracted jobs awaiting audit.
                          </td>
                        </tr>
                      )}
                      {jobs.map((j) => (
                        <tr key={j.jobId} data-testid="subcontract-row" data-ref={j.bookingRef}>
                          <td className="fw-bold">
                            {j.bookingRef}
                            <span className="d-block text-muted small">{j.vesselName || '—'}</span>
                          </td>
                          <td className="small">{j.operatorName || '—'}</td>
                          <td>{j.loggedHours} hrs</td>
                          <td className="fw-bold text-primary">{money(j.calculatedCost)}</td>
                          <td className="text-end">
                            {j.status === 'Approved' ? (
                              <span className="badge bg-success" data-testid="row-approved">
                                <i className="fas fa-check me-1" aria-hidden="true" />
                                Approved
                              </span>
                            ) : (
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => audit(j)}
                                data-testid="audit-verify"
                              >
                                <i className="fas fa-search-dollar me-1" aria-hidden="true" />
                                Audit &amp; Verify
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: verification log */}
          <div className="col-lg-5 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-calculator me-2 text-info" aria-hidden="true" />
                  Cost Verification Log
                </h5>
              </div>
              <div className="card-body">
                {!detail ? (
                  <div className="text-center text-muted py-5 my-4" data-testid="no-subcontract">
                    <i className="fas fa-file-invoice-dollar fa-3x mb-3 text-secondary" aria-hidden="true" />
                    <h6>No subcontract selected</h6>
                    <p className="small">Click “Audit &amp; Verify” to review its base tariff calculations.</p>
                  </div>
                ) : (
                  <div data-testid="verification-log">
                    {/* SoF summary */}
                    <div className="bg-light p-3 rounded small mb-3 border" data-testid="sof-summary">
                      <h6 className="fw-bold text-dark mb-1">
                        <i className="fas fa-ship me-1" aria-hidden="true" />
                        Statement of Facts
                      </h6>
                      <p className="mb-1 text-secondary">
                        Subcontract: <strong>{detail.bookingRef}</strong>
                      </p>
                      <p className="mb-1 text-secondary">
                        Vessel: <strong>{detail.vesselName || '—'}</strong>
                      </p>
                      <p className="mb-1 text-secondary">
                        Affiliate Operator: <strong>{detail.operatorName || '—'}</strong>
                      </p>
                      {detail.sofSignedBy ? (
                        <span className="badge bg-success mt-1" data-testid="sof-signed">
                          <i className="fas fa-signature me-1" aria-hidden="true" />
                          Captain E-signed ({detail.sofSignedBy})
                        </span>
                      ) : (
                        <span className="badge bg-warning text-dark mt-1">SoF not signed</span>
                      )}
                    </div>

                    {/* Base tariff calculation breakdown */}
                    <ul className="list-group list-group-flush small mb-3">
                      <li className="list-group-item d-flex justify-content-between p-2">
                        <span className="text-muted">Logged Duration:</span>
                        <strong data-testid="calc-hours">{detail.loggedHours} hrs</strong>
                      </li>
                      <li className="list-group-item d-flex justify-content-between p-2">
                        <span className="text-muted">Inter-company Hourly Rate:</span>
                        <strong data-testid="calc-rate">{money(detail.hourlyRate)}</strong>
                      </li>
                      <li className="list-group-item d-flex justify-content-between p-2 bg-light border-top">
                        <span className="fw-bold text-dark">Calculated Cost:</span>
                        <strong className="text-primary" data-testid="calc-cost">
                          {money(detail.calculatedCost)}
                        </strong>
                      </li>
                    </ul>

                    {detail.status === 'Approved' ? (
                      <div data-testid="subcontract-approved">
                        <div className="alert alert-success mb-2">
                          <i className="fas fa-lock me-1" aria-hidden="true" />
                          Subcontract costs approved by {detail.approvedBy} —{' '}
                          <strong data-testid="approved-cost">{money(detail.calculatedCost)}</strong>.
                        </div>
                        {/* FR-BIL-5 / FR-AFF-6: raise affiliate billing to SAP as a Sales Order. */}
                        {detail.sapStatus === 'Success' ? (
                          <div className="alert alert-info mb-0 py-2 small" data-testid="affiliate-sap-posted">
                            <i className="fas fa-paper-plane me-1" aria-hidden="true" />
                            Raised to SAP as Sales Order{' '}
                            <strong data-testid="affiliate-sap-so">{detail.sapSalesOrderId}</strong>.
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-outline-primary w-100"
                            onClick={raiseSap}
                            disabled={approving}
                            data-testid="raise-affiliate-sap"
                          >
                            {approving && (
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                            )}
                            <i className="fas fa-paper-plane me-1" aria-hidden="true" />
                            Raise Affiliate Billing to SAP
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="mb-3">
                          <label htmlFor="verifiedHours" className="form-label small fw-bold">
                            Verified Billing Hours
                          </label>
                          <input
                            id="verifiedHours"
                            type="number"
                            min="0"
                            step="0.25"
                            className="form-control"
                            value={verifiedHours}
                            onChange={(e) => setVerifiedHours(e.target.value)}
                            data-testid="verified-hours"
                          />
                        </div>
                        {hoursChanged && (
                          <div className="mb-3">
                            <label htmlFor="adjustmentReason" className="form-label small fw-bold">
                              Adjustment Reason <span className="text-danger">*</span>
                            </label>
                            <textarea
                              id="adjustmentReason"
                              className="form-control"
                              rows={2}
                              value={adjustmentReason}
                              onChange={(e) => setAdjustmentReason(e.target.value)}
                              placeholder="Required when changing logged hours"
                              data-testid="adjustment-reason"
                            />
                          </div>
                        )}
                        <button
                          className="btn btn-success w-100"
                          onClick={approve}
                          disabled={approving || (hoursChanged && !adjustmentReason.trim())}
                          data-testid="approve-subcontract"
                        >
                          {approving && (
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            />
                          )}
                          <i className="fas fa-check-double me-1" aria-hidden="true" />
                          Approve Subcontract Costs
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
